import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Zap, MapPin, Clock, Save, Radio } from "lucide-react";
import { MapContainer, TileLayer, Circle, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix marker icon (React-Leaflet + Webpack quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const AKL = [-36.8485, 174.7633];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function CenterPicker({ center, radius, onCenter }) {
  useMapEvents({ click: (e) => onCenter([e.latlng.lat, e.latlng.lng]) });
  return (
    <>
      <Marker position={center} />
      <Circle
        center={center}
        radius={radius * 1000}
        pathOptions={{ color: "#E5A93C", fillColor: "#E5A93C", fillOpacity: 0.15 }}
      />
    </>
  );
}

const emptyRule = {
  name: "", active: true,
  center_lat: AKL[0], center_lng: AKL[1], radius_km: 5,
  multiplier: 1.5,
  time_start: "07:00", time_end: "10:00",
  days_of_week: [0, 1, 2, 3, 4], // Mon-Fri
  priority: 100, note: "",
};

function SurgeForm({ form, setForm, onSubmit, submitLabel }) {
  const toggleDay = (i) => {
    const days = form.days_of_week || [];
    setForm({ ...form, days_of_week: days.includes(i) ? days.filter((d) => d !== i) : [...days, i].sort() });
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-5 max-h-[75vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2"><Label className="text-xs uppercase text-slate-400">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="AKL Airport Fri-Sun evening" className="bg-black/30 border-white/10 mt-1 h-10" data-testid="surge-name" /></div>
        <div><Label className="text-xs uppercase text-slate-400">Multiplier ×</Label><Input required type="number" step="0.1" min="1" max="5" value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: Number(e.target.value) })} className="bg-black/30 border-white/10 mt-1 h-10 font-mono" data-testid="surge-multiplier" /></div>
      </div>

      <div>
        <Label className="text-xs uppercase text-slate-400 flex items-center gap-2"><MapPin className="w-3 h-3" /> Geofence — click on map to set centre</Label>
        <div className="mt-2 rounded-lg overflow-hidden border border-white/10 h-64">
          <MapContainer center={[form.center_lat, form.center_lng]} zoom={11} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <CenterPicker
              center={[form.center_lat, form.center_lng]}
              radius={form.radius_km}
              onCenter={([lat, lng]) => setForm({ ...form, center_lat: lat, center_lng: lng })}
            />
          </MapContainer>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">Lat</Label><Input type="number" step="0.0001" value={form.center_lat} onChange={(e) => setForm({ ...form, center_lat: Number(e.target.value) })} className="bg-black/30 border-white/10 h-9 mt-1 font-mono text-xs" data-testid="surge-lat" /></div>
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">Lng</Label><Input type="number" step="0.0001" value={form.center_lng} onChange={(e) => setForm({ ...form, center_lng: Number(e.target.value) })} className="bg-black/30 border-white/10 h-9 mt-1 font-mono text-xs" data-testid="surge-lng" /></div>
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">Radius (km)</Label><Input type="number" min="0.5" max="50" step="0.5" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: Number(e.target.value) })} className="bg-black/30 border-white/10 h-9 mt-1 font-mono text-xs" data-testid="surge-radius" /></div>
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <Label className="text-xs uppercase text-slate-400 flex items-center gap-2"><Clock className="w-3 h-3" /> Time window</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">Start</Label><Input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} className="bg-black/30 border-white/10 h-10 mt-1 font-mono" data-testid="surge-time-start" /></div>
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">End</Label><Input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} className="bg-black/30 border-white/10 h-10 mt-1 font-mono" data-testid="surge-time-end" /></div>
        </div>
        <div className="flex gap-1.5 mt-3 flex-wrap">
          {DAYS.map((d, i) => (
            <button
              type="button"
              key={i}
              onClick={() => toggleDay(i)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest ${
                (form.days_of_week || []).includes(i)
                  ? "bg-amber-400 text-[#090D14]"
                  : "bg-black/30 border border-white/10 text-slate-400 hover:text-white"
              }`}
              data-testid={`surge-day-${i}`}
            >
              {d}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Overnight windows OK (e.g. 22:00 → 04:00). Leave empty days list unchecked = never.</p>
      </div>

      <div className="border-t border-white/5 pt-4 grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase text-slate-400">Priority</Label><Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} className="bg-black/30 border-white/10 h-10 mt-1 font-mono" /></div>
        <div className="flex items-end gap-3">
          <div className="flex-1"><Label className="text-xs uppercase text-slate-400">Active</Label><div className="mt-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="surge-active" /></div></div>
        </div>
      </div>

      <Button type="submit" className="btn-gold" data-testid="surge-submit"><Save className="w-4 h-4 mr-2" /> {submitLabel}</Button>
    </form>
  );
}

export default function SurgeTab() {
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRule);

  // Preview widget
  const [tLat, setTLat] = useState(AKL[0]);
  const [tLng, setTLng] = useState(AKL[1]);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/surge-rules"); setRules(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/surge-rules", form);
      toast.success("Rule created"); setOpen(false); setForm(emptyRule); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyRule, ...r }); };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/surge-rules/${editing.id}`, form);
      toast.success("Rule saved"); setEditing(null); setForm(emptyRule); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this surge rule?")) return;
    try { await api.delete(`/admin/surge-rules/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const toggle = async (r) => {
    try { await api.patch(`/admin/surge-rules/${r.id}`, { active: !r.active }); load(); }
    catch { toast.error("Failed"); }
  };
  const runPreview = async () => {
    try {
      const { data } = await api.get(`/admin/surge-rules/preview?lat=${tLat}&lng=${tLng}`);
      setPreview(data);
    } catch { toast.error("Preview failed"); }
  };

  return (
    <div>
      <Header title="Dynamic pricing" subtitle="Surge multipliers by geofence + time window">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyRule); }}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="surge-add"><Plus className="w-4 h-4 mr-2" /> New rule</Button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">New surge rule</DialogTitle></DialogHeader>
            <SurgeForm form={form} setForm={setForm} onSubmit={add} submitLabel="Create rule" />
          </DialogContent>
        </Dialog>
      </Header>

      {/* Preview */}
      <div className="glass-card gold-border rounded-2xl p-5 mb-6" data-testid="surge-preview-card">
        <div className="flex items-center gap-2 mb-3">
          <Radio className="w-4 h-4 text-amber-400" />
          <div className="text-sm font-semibold text-amber-300">Live preview — test a pickup coordinate</div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">Lat</Label><Input type="number" step="0.0001" value={tLat} onChange={(e) => setTLat(Number(e.target.value))} className="bg-black/30 border-white/10 h-10 mt-1 font-mono text-xs" data-testid="surge-preview-lat" /></div>
          <div><Label className="text-[10px] uppercase text-slate-500 font-mono">Lng</Label><Input type="number" step="0.0001" value={tLng} onChange={(e) => setTLng(Number(e.target.value))} className="bg-black/30 border-white/10 h-10 mt-1 font-mono text-xs" data-testid="surge-preview-lng" /></div>
          <Button onClick={runPreview} className="btn-gold" data-testid="surge-preview-run">Check surge now</Button>
          <div className="text-right">
            {preview && (preview.active ? (
              <div><div className="text-[10px] uppercase text-slate-500 font-mono">Surge active</div>
                   <div className="font-display text-2xl font-extrabold text-amber-400" data-testid="surge-preview-result">×{preview.multiplier}</div>
                   <div className="text-xs text-slate-500">{preview.rule?.name}</div></div>
            ) : <div className="text-slate-500 text-sm">No surge at this time</div>)}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {rules.length === 0 && (
          <div className="glass-card rounded-2xl p-8 gold-border text-center col-span-full" data-testid="surge-empty">
            <Zap className="w-8 h-8 text-amber-400 mx-auto mb-2 opacity-50" />
            <div className="text-slate-400">No surge rules yet — every ride prices at 1.0×.</div>
          </div>
        )}
        {rules.map((r) => (
          <div key={r.id} className="glass-card rounded-2xl p-5 gold-border" data-testid={`surge-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="font-display font-bold">{r.name}</span>
                  <Badge className={`${r.active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-700/40 text-slate-400 border-slate-600/40"} border font-mono uppercase text-[10px] tracking-wider`}>{r.active ? "ON" : "OFF"}</Badge>
                </div>
                <div className="text-3xl font-display font-extrabold text-amber-400 mt-2">×{r.multiplier}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`surge-toggle-${r.id}`} />
                <div className="flex gap-1">
                  <Button variant="outline" size="icon" onClick={() => openEdit(r)} className="bg-transparent border-white/10 text-slate-300 h-8 w-8" data-testid={`surge-edit-${r.id}`}>✎</Button>
                  <Button variant="outline" size="icon" onClick={() => del(r.id)} className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8" data-testid={`surge-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="bg-black/20 rounded p-2"><div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">Geofence</div><div className="text-slate-300 font-mono">{r.center_lat.toFixed(3)}, {r.center_lng.toFixed(3)} · {r.radius_km} km</div></div>
              <div className="bg-black/20 rounded p-2"><div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono">Window</div><div className="text-slate-300 font-mono">{r.time_start} → {r.time_end}</div></div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {DAYS.map((d, i) => (
                <span key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${(r.days_of_week || []).includes(i) ? "bg-amber-400/20 text-amber-300" : "text-slate-600"}`}>{d}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyRule); } }}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Edit — {editing?.name}</DialogTitle></DialogHeader>
          <SurgeForm form={form} setForm={setForm} onSubmit={save} submitLabel="Save changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
